import { createTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

// Import all font files
import EuclidCircularARegular from '../assets/fonts/Euclid Circular A Regular.ttf';
import EuclidCircularABold from '../assets/fonts/Euclid Circular A Bold.ttf';
import EuclidCircularALight from '../assets/fonts/Euclid Circular A Light.ttf';
import EuclidCircularAMedium from '../assets/fonts/Euclid Circular A Medium.ttf';
import EuclidCircularASemiBold from '../assets/fonts/Euclid Circular A SemiBold.ttf';
import EuclidCircularAItalic from '../assets/fonts/Euclid Circular A Italic.ttf';
import EuclidCircularABoldItalic from '../assets/fonts/Euclid Circular A Bold Italic.ttf';
import EuclidCircularALightItalic from '../assets/fonts/Euclid Circular A Light Italic.ttf';
import EuclidCircularAMediumItalic from '../assets/fonts/Euclid Circular A Medium Italic.ttf';
import EuclidCircularASemiBoldItalic from '../assets/fonts/Euclid Circular A SemiBold Italic.ttf';

export const colors = {
  // Legacy tokens (retained for status indicators and migration)
  midnightGreen: '#014953',
  pink: '#ff2386',
  bone: '#e4dac9',
  boneLight: '#f5f1eb',
  candelion: '#ace1af',
  lemonLime: '#e3ff02',
  headerBg: '#282c34',
  linkBlue: '#61dafb',
  // New CI tokens (Marketing-Site-CI-Design-Specification)
  deepBlue: '#090d2e',
  sand: '#f9f4e6',
  lavender: '#9b84ff',
  lavenderDeep: '#7a63ff',
  lavenderCard: '#5e4be0',
  // Punctual accents — small UI moments only (§4.1a)
  lime: '#d8ff62',
  vermilion: '#ff5500',
  warning: '#9a6700',
  reviewWarning: '#ac6500',
  black: '#000000',
  inkPrimary: '#090d2e',
  inkSecondary: '#3f4561',
  inkTertiary: '#6b7290',
  snowSecondary: '#c9ccdb',
  snowTertiary: '#8b90a9',
} as const;

/**
 * The corporate typeface (CD Guide 2026-04-01, §Typography): Euclid Circular A,
 * self-hosted. The CD's Poppins fallback is scoped to Google Presentations and
 * does not apply on the web — system sans-serif covers the `font-display: swap`
 * gap instead.
 */
export const fontStack =
  '"Euclid Circular A", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

declare module '@mui/material/styles' {
  interface Theme {
    colors: typeof colors;
  }
  interface ThemeOptions {
    colors?: typeof colors;
  }
}

/**
 * Additive visual-language primitives — spacing, type, surface, radii/focus
 * and motion values sourced from the flow prototype
 * (`product-prototypes/new-prototype/flow/flow-chrome.css` and
 * `.../public/prototypes/shared/tokens.css`). Nothing existing reads this
 * key; it is consumed only by `gchat/src/styles/visualLanguage.ts`'s sx
 * fragments and their consumers.
 */
export const visualPrimitiveTokens = {
  // Layout
  columnMaxWidth: '560px', // flow-chrome.css:47 — prototype's own value is `min(560px, 92vw)`; the 92vw half is dropped here because that column is a direct child of an unpadded full-bleed overlay in the prototype, while both advisory consumers sit inside containers that already carry their own page padding, which supplies the narrow-viewport gutter instead
  headingGap: '28px', // flow-chrome.css:48
  rowPaddingY: '24px', // flow-chrome.css:51
  rowGap: '6px', // flow-chrome.css:52
  pageMinHeight: 'calc(100vh - 120px)', // carried over verbatim from AdvisoryEngagementPage's earlier inline minHeight literal (reused, not invented); no prototype equivalent

  // Type
  titleSize: '22px', // flow-chrome.css:54
  titleWeight: 500, // flow-chrome.css:54
  titleTracking: '-0.01em', // flow-chrome.css:54
  headingSize: '20px', // equals MUI h6's 1.25rem
  headingWeight: 500, // theme.typography.h6.fontWeight
  metaSize: '14px', // flow-chrome.css:55
  metaLineHeight: 1.5, // flow-chrome.css:55
  eyebrowSize: '12px', // brief/styles.css:36
  eyebrowWeight: 500, // brief/styles.css:37
  eyebrowTracking: '0.18em', // brief/styles.css:37
  monoStack: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace", // tokens.css:80 minus JetBrains Mono (self-hosting deferred)
  actionSize: '15px', // flow-chrome.css:10
  actionWeight: 500, // flow-chrome.css:10
  actionPadding: '12px 26px', // flow-chrome.css:11

  // Surface
  border: alpha(colors.deepBlue, 0.1), // tokens.css:72
  borderLight: alpha(colors.deepBlue, 0.055), // tokens.css:73

  // Radii / focus
  radiusPill: '999px', // tokens.css:83
  focusRingWidth: '2px', // flow-chrome.css:30
  focusRingOffset: '3px', // flow-chrome.css:30
  focusRingRadius: '2px', // flow-chrome.css:30 — deliberately unconsumed by the shared focusRing fragment, available for per-consumer opt-in

  // Motion
  ease: 'cubic-bezier(.33,.9,.3,1)', // tokens.css:96
  tFast: '140ms', // tokens.css:97
  tMed: '220ms', // tokens.css:98 — encoded ahead of its consumers, deliberately unused for now
  pressScale: 'scale(0.98)', // flow-chrome.css:14

  // Wizard chrome
  wizTopPaddingY: '22px', // .wiz-top
  wizTopGap: '16px', // .wiz-top
  stepSize: '12px', // .wiz-top .step
  leaveSize: '14px', // .wiz-top .leave
  wizSceneGapTop: '64px', // .wiz-scene
  wizSceneGapBottom: '96px', // .wiz-scene
  wizEyebrowGap: '22px', // .wiz-eyebrow
  questionSize: 'clamp(28px, 4vw, 44px)', // .wiz-q
  questionWeight: 500, // .wiz-q
  questionTracking: '-0.02em', // .wiz-q
  questionLineHeight: 1.15, // .wiz-q
  hintGap: '16px', // .wiz-hint
  hintSize: '16px', // .wiz-hint
  hintMaxWidth: '34em', // .wiz-hint
  hintLineHeight: 1.6, // .wiz-hint
  wizNavGap: '48px', // .wiz-nav
  wizNavItemGap: '22px', // .wiz-nav
  kbdHintSize: '11.5px', // .wiz-nav .kbd-hint
  inputSize: '22px', // .wiz-input
  inputLineHeight: 1.4, // .wiz-input
  inputGap: '40px', // .wiz-input
  inputPaddingY: '8px 0 14px', // .wiz-input

  // Run / thinking scene
  runInnerMaxWidth: '640px', // .run-inner
  runInnerGap: '16px', // .run-inner
  runTitleSize: 'clamp(26px, 4vw, 40px)', // .run-title
  runTitleWeight: 500, // .run-title
  runTitleTracking: '-0.02em', // .run-title
  runTitleLineHeight: 1.15, // .run-title
  runLineSize: '16px', // .run-line
  runLineLineHeight: 1.6, // .run-line
  runNoteSize: '13px', // .run-note
  runNoteLineHeight: 1.6, // .run-note

  // Goal read-only
  goalReadOnlyGap: '40px', // .wiz-goal-ro
  goalReadOnlySize: '22px', // .wiz-goal-ro
  goalReadOnlyLineHeight: 1.5, // .wiz-goal-ro
  goalReadOnlyBorderWidth: '2px', // .wiz-goal-ro
  goalReadOnlyPadding: '6px 0 6px 24px', // .wiz-goal-ro
  goalReadOnlyMaxWidth: '640px', // .wiz-goal-ro

  // Fork
  forkHintGap: '48px', // .wiz-fork-hint
  forkAltSize: '15px', // .wiz-fork-alt
  forkAltPaddingBottom: '2px', // .wiz-fork-alt

  // Confirm lists
  clHeaderGap: '44px', // flow-chrome.css:169 .cl-h
  clAntiHeaderGap: '56px', // flow-chrome.css:170 .cl-h.anti
  clListGap: '6px', // flow-chrome.css:172 .cl-list
  clRowColumns: '40px 1fr auto', // flow-chrome.css:174 .cl-row
  clRowGap: '16px', // flow-chrome.css:174 .cl-row
  clRowPadding: '15px 4px', // flow-chrome.css:175 .cl-row
  clIndexSize: '12px', // flow-chrome.css:178 .cl-row .n
  clTextSize: '16px', // flow-chrome.css:180 .cl-row .t
  clTextLineHeight: 1.55, // flow-chrome.css:180 .cl-row .t
  clRemoveSize: '13px', // flow-chrome.css:182 .cl-row .rm
  clEmptyGap: '18px', // flow-chrome.css:184 .cl-empty
  clEmptySize: '15px', // flow-chrome.css:184 .cl-empty
  clEmptyLineHeight: 1.6, // flow-chrome.css:184 .cl-empty
  clEmptyMaxWidth: '34em', // flow-chrome.css:184 .cl-empty

  // Key-question cards
  kqListGap: '40px', // flow-chrome.css:187-205 .kq-list
  kqCardPadding: '22px 4px 10px', // flow-chrome.css:187-205 .kq-card
  kqRowColumns: '52px 1fr auto', // flow-chrome.css:187-205 .kq-head/.kq-sub
  kqRowGap: '16px', // flow-chrome.css:187-205 .kq-head
  kqIndexSize: '12px', // flow-chrome.css:187-205 .kq-head .n
  kqTextSize: '18px', // flow-chrome.css:187-205 .kq-head .t
  kqTextWeight: 500, // flow-chrome.css:187-205 .kq-head .t
  kqTextTracking: '-0.005em', // flow-chrome.css:187-205 .kq-head .t
  kqTextLineHeight: 1.35, // flow-chrome.css:187-205 .kq-head .t
  kqSubPaddingY: '11px', // flow-chrome.css:187-205 .kq-sub
  kqSubIndexSize: '11.5px', // flow-chrome.css:187-205 .kq-sub .n
  kqSubIndexGap: '8px', // flow-chrome.css:187-205 .kq-sub .n
  kqSubTextSize: '15px', // flow-chrome.css:187-205 .kq-sub .t
  kqSubTextLineHeight: 1.55, // flow-chrome.css:187-205 .kq-sub .t

  // Evidence dropzone
  radiusCard: '16px', // tokens.css:84 --r-card
  dzMarginTop: '44px', // flow-chrome.css:220 .dropzone
  dzPadding: '44px 24px', // flow-chrome.css:222 .dropzone
  dzTitleSize: '17px', // flow-chrome.css:226 .dropzone .dz-t
  dzTitleWeight: 500, // flow-chrome.css:226 .dropzone .dz-t
  dzSubGap: '8px', // flow-chrome.css:227 .dropzone .dz-s
  dzSubSize: '14px', // flow-chrome.css:227 .dropzone .dz-s

  // Composite Brief — sourced from product-prototypes@11ddd324's
  // prototypes/brief/styles.css. These remain additive, route-scoped values:
  // the existing 560px advisory wizard column and every non-advisory route are
  // deliberately unaffected.
  briefColumnMaxWidth: '880px',
  briefSpreadMinHeight: '92vh',
  briefSpreadPaddingY: '96px',
  briefPageGutter: '24px',
  briefCoverTitleSize: 'clamp(34px, 8vw, 84px)',
  briefCoverTitleTracking: '-0.025em',
  briefCoverTitleLineHeight: 1.08,
  briefCoverSubtitleSize: '19px',
  briefChapterTitleSize: 'clamp(32px, 4.6vw, 52px)',
  briefChapterTitleTracking: '-0.02em',
  briefChapterTitleLineHeight: 1.12,
  briefBodySize: '17px',
  briefBodyLineHeight: 1.65,
  briefSummaryValueSize: '21px',
  briefSectionGap: '56px',
  briefFigureGap: '64px',
  briefTableHeaderSize: '12px',
  briefTableCellSize: '16px',
  briefCalloutPadding: '6px 0 6px 28px',
} as const;

declare module '@mui/material/styles' {
  interface Theme {
    visualPrimitives: typeof visualPrimitiveTokens;
  }
  interface ThemeOptions {
    visualPrimitives?: typeof visualPrimitiveTokens;
  }
}

const globalStyles = {
  '@global': {
    '@font-face': [
      {
        fontFamily: 'Euclid Circular A',
        src: `url(${EuclidCircularALight}) format('truetype')`,
        fontWeight: 300,
        fontStyle: 'normal',
        fontDisplay: 'swap',
      },
      {
        fontFamily: 'Euclid Circular A',
        src: `url(${EuclidCircularALightItalic}) format('truetype')`,
        fontWeight: 300,
        fontStyle: 'italic',
        fontDisplay: 'swap',
      },
      {
        fontFamily: 'Euclid Circular A',
        src: `url(${EuclidCircularARegular}) format('truetype')`,
        fontWeight: 400,
        fontStyle: 'normal',
        fontDisplay: 'swap',
      },
      {
        fontFamily: 'Euclid Circular A',
        src: `url(${EuclidCircularAItalic}) format('truetype')`,
        fontWeight: 400,
        fontStyle: 'italic',
        fontDisplay: 'swap',
      },
      {
        fontFamily: 'Euclid Circular A',
        src: `url(${EuclidCircularAMedium}) format('truetype')`,
        fontWeight: 500,
        fontStyle: 'normal',
        fontDisplay: 'swap',
      },
      {
        fontFamily: 'Euclid Circular A',
        src: `url(${EuclidCircularAMediumItalic}) format('truetype')`,
        fontWeight: 500,
        fontStyle: 'italic',
        fontDisplay: 'swap',
      },
      {
        fontFamily: 'Euclid Circular A',
        src: `url(${EuclidCircularASemiBold}) format('truetype')`,
        fontWeight: 600,
        fontStyle: 'normal',
        fontDisplay: 'swap',
      },
      {
        fontFamily: 'Euclid Circular A',
        src: `url(${EuclidCircularASemiBoldItalic}) format('truetype')`,
        fontWeight: 600,
        fontStyle: 'italic',
        fontDisplay: 'swap',
      },
      {
        fontFamily: 'Euclid Circular A',
        src: `url(${EuclidCircularABold}) format('truetype')`,
        fontWeight: 700,
        fontStyle: 'normal',
        fontDisplay: 'swap',
      },
      {
        fontFamily: 'Euclid Circular A',
        src: `url(${EuclidCircularABoldItalic}) format('truetype')`,
        fontWeight: 700,
        fontStyle: 'italic',
        fontDisplay: 'swap',
      },
    ],
  },
};

export const theme = createTheme({
  colors,
  visualPrimitives: visualPrimitiveTokens,
  palette: {
    primary: {
      main: colors.lavender,
      dark: colors.lavenderDeep,
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.lavenderCard,
      contrastText: '#ffffff',
    },
    background: {
      default: '#ffffff',
      paper: colors.sand,
    },
    text: {
      primary: colors.inkPrimary,
      secondary: colors.inkSecondary,
      disabled: colors.snowTertiary,
    },
    divider: colors.snowSecondary,
  },
  typography: {
    // Body / UI: Euclid Circular A Regular per CD Guide §Typography
    fontFamily: fontStack,
    h1: {
      fontFamily: fontStack,
      fontWeight: 700,
    },
    h2: {
      fontFamily: fontStack,
      fontWeight: 600,
    },
    h3: {
      fontFamily: fontStack,
      fontWeight: 500,
    },
    h4: {
      fontFamily: fontStack,
      fontWeight: 500,
    },
    h5: {
      fontFamily: fontStack,
      fontWeight: 500,
    },
    h6: {
      fontFamily: fontStack,
      fontWeight: 500,
    },
    body1: {
      fontWeight: 400,
      lineHeight: 1.6,
    },
    body2: {
      fontWeight: 400,
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        ${globalStyles['@global']['@font-face']
          .map(
            font => `
          @font-face {
            font-family: ${font.fontFamily};
            src: ${font.src};
            font-weight: ${font.fontWeight};
            font-style: ${font.fontStyle};
            font-display: ${font.fontDisplay};
          }
        `
          )
          .join('\n')}

        html, body {
          margin: 0;
          padding: 0;
          height: 100%;
          font-family: ${fontStack};
          line-height: 1.6;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

/* Ensure .app fills the full viewport height */
.app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  height: 100%;
  font-family: ${fontStack};
}

/* Ensure .main-content fills the remaining space */
/* This rule must stay closed immediately after its media query: it
   previously opened without ever being closed, so every rule below it -
   .App*, both @keyframes blocks and .markdown-content - was nested inside
   .main-content and depended on stylis' error-recovery behaviour rather
   than on valid CSS. Everything from .App onward must remain deliberately
   top-level. */
.main-content {
  flex: 1; /* Takes up the remaining space */
  padding: 0;
  background-color: ${colors.sand};

  @media (max-width: 768px) {
    padding: 0;
    height: 100%; /* Ensures it stretches to the parent's height */
  }
}

.App {
  text-align: center;
}

.App-logo {
  height: 40vmin;
  pointer-events: none;
}

@media (prefers-reduced-motion: no-preference) {
  .App-logo {
    animation: App-logo-spin infinite 20s linear;
  }
}

.App-header {
  background-color: ${colors.headerBg};
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: calc(10px + 2vmin);
  color: white;
}

.App-link {
  color: ${colors.linkBlue};
}

@keyframes App-logo-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes rotating {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.rotating {
  animation: rotating 2s linear infinite;
}

.markdown-content {
  font-family: inherit;
}

.markdown-content p {
  margin-bottom: 1em;
}

        /* No global font-family rule here: an !important on * would override
           stylesheets added later, including the prototype's composites.css. */
      `,
    },
  },
});
